import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Division,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Paragraph,
  Spin,
  useToast,
  type FormInstance,
} from "@ir/ui";
import {
  CreateMasterDataPurchaserDTO,
  GetMasterDataProductCategoriesDTO,
  UpdateMasterDataPurchaserDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataPurchaser } from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataPurchaserUseCase,
  GetMasterDataProductCategoriesUseCase,
  UpdateMasterDataPurchaserUseCase,
} from "@src/application/useCases/masterData";
import {
  GetBrandAssociatedPurchasersLookupUseCase,
  GetPurchaserConfigTemplateUseCase,
} from "@src/application/useCases/purchaser";
import {
  MasterDataProductCategoryRepository,
  MasterDataPurchaserRepository,
} from "@src/infrastructure/repositories/masterData";
import { PurchaserRepository } from "@src/infrastructure/repositories/purchaser";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useMasterDataBrand } from "../MasterDataBrandContext";
import { useMasterDataPurchasers } from "@src/presentation/providers/MasterDataPurchasersDataProvider";
import { invalidateMasterDataListCacheScopes } from "@src/presentation/providers/masterData/masterDataListCache";
import {
  getCachedPurchaserConfigTemplate,
  getOrFetchPurchaserConfigTemplate,
  hasCachedPurchaserConfigTemplate,
  setCachedPurchaserConfigTemplate,
} from "@src/presentation/providers/purchaserConfigTemplateCache";
import { parseOptionalJsonField, formatJsonForTextarea } from "@src/presentation/pages/Purchasers/utils/purchaserConfigTemplateFormUtils";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import { MasterDataJsonPreviewModal } from "../utils/MasterDataJsonPreviewModal";
import { MasterDataPurchaserDcModal } from "../utils/MasterDataPurchaserDcModal";
import {
  MasterDataPurchaserAddEditForm,
  type CatalogPurchaserSelectOption,
  type ProductCategorySelectOption,
  type PurchaserAddEditFormValues,
} from "../utils/MasterDataPurchaserAddEditForm";
import {
  MasterDataPurchasersMatrixTable,
  type MasterDataPurchaserDcTarget,
} from "../utils/MasterDataPurchasersMatrixTable";
import {
  EMPTY_PURCHASER_FORM_VALUES,
  numberToFormValue,
  parseOptionalIntegerField,
  parseOptionalPercentField,
  TERM_DAYS_FIELD_MAX,
} from "../utils/masterDataPurchaserFormUtils";
import { isMasterDataSaveNetworkError } from "../utils/masterDataSaveErrors";
import {
  isPurchaserKeyDuplicateError,
  PURCHASER_KEY_ALREADY_EXISTS_MESSAGE,
  PURCHASER_SAVE_NETWORK_ERROR_MESSAGE,
} from "../utils/purchaserKeyValidation";
import {
  useMasterDataAddEditModal,
  type MasterDataAddEditModalMode,
} from "../utils/useMasterDataAddEditModal";

function toOptionalCategoryId(value: number | null | undefined): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function resolvePurchaserIdentity(
  values: PurchaserAddEditFormValues,
  modalMode: MasterDataAddEditModalMode | null,
  catalogOptions: CatalogPurchaserSelectOption[],
): { purchaserName: string; purchaserKey: string } | null {
  const selected =
    modalMode === "create"
      ? catalogOptions.find(
          (option) => option.purchaserId === values.catalogPurchaserId,
        )
      : undefined;
  const purchaserName =
    selected?.purchaserName.trim() || values.purchaserName.trim();
  const purchaserKey =
    selected?.purchaserKey.trim() || values.purchaserKey.trim();
  if (!purchaserName || !purchaserKey) {
    return null;
  }
  return { purchaserName, purchaserKey };
}

function parsePurchaserSaveFields(values: PurchaserAddEditFormValues) {
  return {
    searchKeywords: values.searchKeywords.trim() || null,
    defaultProductCategoryId: toOptionalCategoryId(
      values.defaultProductCategoryId,
    ),
    metadata: parseOptionalJsonField(values.metadata, "Metadata"),
    intelligenceFilter: parseOptionalJsonField(
      values.intelligenceFilter,
      "Intelligence Filter",
    ),
    deductionTypeInvoicePatternForDiscount: parseOptionalJsonField(
      values.deductionTypeInvoicePatternForDiscount,
      "Deduction Invoice Pattern",
    ),
    additionalFieldMapping: parseOptionalJsonField(
      values.additionalFieldMapping,
      "Additional Field Mapping",
    ),
    discountPercentageCredit: parseOptionalPercentField(
      values.discountPercentageCredit,
      "Credit Term Discount %",
    ),
    discountPercentageDeduction: parseOptionalPercentField(
      values.discountPercentageDeduction,
      "Deduction Term Discount %",
    ),
    discountTermAgreementDays: parseOptionalIntegerField(
      values.discountTermAgreementDays,
      "Term Agreement Days",
      TERM_DAYS_FIELD_MAX,
    ),
    discountTermAgreementDaysTolerance: parseOptionalIntegerField(
      values.discountTermAgreementDaysTolerance,
      "Term Days Tolerance",
    ),
    isActive: Boolean(values.isActive),
  };
}

function applyPurchaserSaveError(
  err: unknown,
  form: FormInstance<PurchaserAddEditFormValues>,
  modalMode: MasterDataAddEditModalMode | null,
  showError: (content: string) => void,
  onInvalid: () => void,
) {
  if (isPurchaserKeyDuplicateError(err)) {
    form.setFields([
      {
        name: modalMode === "create" ? "catalogPurchaserId" : "purchaserKey",
        errors: [PURCHASER_KEY_ALREADY_EXISTS_MESSAGE],
      },
    ]);
    onInvalid();
    return;
  }
  if (isMasterDataSaveNetworkError(err)) {
    showError(PURCHASER_SAVE_NETWORK_ERROR_MESSAGE);
    return;
  }
  showError(
    err instanceof Error
      ? err.message
      : "Failed to save purchaser. Please try again.",
  );
}

export const MasterDataPurchasersPage = () => {
  const { brand } = useMasterDataBrand();
  const brandId = brand?.brandId ?? null;
  const { purchasers, loading, refetch, brandKey } = useMasterDataPurchasers();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<PurchaserAddEditFormValues>();

  const {
    modalMode,
    editingEntity: editingPurchaser,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataPurchaser>();

  const [jsonPreview, setJsonPreview] = useState<{
    title: string;
    value: unknown;
  } | null>(null);
  const [dcPreview, setDcPreview] = useState<MasterDataPurchaserDcTarget | null>(
    null,
  );

  const [catalogOptions, setCatalogOptions] = useState<
    CatalogPurchaserSelectOption[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const catalogLoadedRef = useRef(false);

  const [categoryOptions, setCategoryOptions] = useState<
    ProductCategorySelectOption[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const categoriesLoadedRef = useRef(false);

  const purchaserRepository = useMemo(
    () => new MasterDataPurchaserRepository(),
    [],
  );
  const categoryRepository = useMemo(
    () => new MasterDataProductCategoryRepository(),
    [],
  );
  const catalogRepository = useMemo(() => new PurchaserRepository(), []);
  const createUseCase = useMemo(
    () => new CreateMasterDataPurchaserUseCase(purchaserRepository),
    [purchaserRepository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataPurchaserUseCase(purchaserRepository),
    [purchaserRepository],
  );
  const getCategoriesUseCase = useMemo(
    () => new GetMasterDataProductCategoriesUseCase(categoryRepository),
    [categoryRepository],
  );
  const getBrandAssociatedPurchasersLookupUseCase = useMemo(
    () => new GetBrandAssociatedPurchasersLookupUseCase(catalogRepository),
    [catalogRepository],
  );
  const getConfigTemplateUseCase = useMemo(
    () => new GetPurchaserConfigTemplateUseCase(catalogRepository),
    [catalogRepository],
  );

  const existingPurchaserKeys = useMemo(() => {
    const keys = new Set(
      purchasers.map((purchaser) => purchaser.purchaserKey.toUpperCase()),
    );
    return keys;
  }, [purchasers]);

  const initialValues = useMemo((): PurchaserAddEditFormValues => {
    if (modalMode === "edit" && editingPurchaser) {
      return {
        catalogPurchaserId: null,
        purchaserName: editingPurchaser.purchaserName,
        purchaserKey: editingPurchaser.purchaserKey,
        isActive: editingPurchaser.isActive,
        searchKeywords: editingPurchaser.searchKeywords ?? "",
        defaultProductCategoryId: editingPurchaser.defaultProductCategoryId,
        metadata: formatJsonForTextarea(editingPurchaser.metadata),
        intelligenceFilter: formatJsonForTextarea(
          editingPurchaser.intelligenceFilter,
        ),
        discountPercentageCredit: numberToFormValue(
          editingPurchaser.discountPercentageCredit,
        ),
        deductionTypeInvoicePatternForDiscount: formatJsonForTextarea(
          editingPurchaser.deductionTypeInvoicePatternForDiscount,
        ),
        discountPercentageDeduction: numberToFormValue(
          editingPurchaser.discountPercentageDeduction,
        ),
        discountTermAgreementDays: numberToFormValue(
          editingPurchaser.discountTermAgreementDays,
        ),
        additionalFieldMapping: formatJsonForTextarea(
          editingPurchaser.additionalFieldMapping,
        ),
        discountTermAgreementDaysTolerance: numberToFormValue(
          editingPurchaser.discountTermAgreementDaysTolerance,
        ),
      };
    }
    return EMPTY_PURCHASER_FORM_VALUES;
  }, [editingPurchaser, modalMode]);

  const mergeEditingCategory = useCallback(
    (
      options: ProductCategorySelectOption[],
      purchaser: MasterDataPurchaser | null,
    ): ProductCategorySelectOption[] => {
      if (
        purchaser?.defaultProductCategoryId == null ||
        !purchaser.defaultProductCategoryName
      ) {
        return options;
      }
      if (
        options.some(
          (option) =>
            option.productCategoryId === purchaser.defaultProductCategoryId,
        )
      ) {
        return options;
      }
      return [
        {
          productCategoryId: purchaser.defaultProductCategoryId,
          name: purchaser.defaultProductCategoryName,
          categoryKey: "",
        },
        ...options,
      ];
    },
    [],
  );

  const ensureCategoriesForModal = useCallback(async () => {
    if (categoriesLoadedRef.current) {
      setCategoryOptions((prev) =>
        mergeEditingCategory(prev, editingPurchaser),
      );
      return;
    }

    setCategoriesLoading(true);
    try {
      const result = await getCategoriesUseCase.execute(
        new GetMasterDataProductCategoriesDTO({
          search: null,
          includeInactive: false,
        }),
      );
      const options: ProductCategorySelectOption[] = result.items.map(
        (category) => ({
          productCategoryId: category.productCategoryId,
          name: category.name,
          categoryKey: category.categoryKey,
        }),
      );
      setCategoryOptions(mergeEditingCategory(options, editingPurchaser));
      categoriesLoadedRef.current = true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load categories";
      errorToast({ content: message });
      setCategoryOptions([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [editingPurchaser, errorToast, getCategoriesUseCase, mergeEditingCategory]);

  useEffect(() => {
    catalogLoadedRef.current = false;
    setCatalogOptions([]);
  }, [brandId]);

  const ensureCatalogPurchasersForModal = useCallback(async () => {
    if (modalMode !== "create") return;
    if (brandId == null) return;
    if (catalogLoadedRef.current) return;

    setCatalogLoading(true);
    try {
      // Brand-associated catalog purchasers only (tbl_org_brand_purchasers).
      const { items } =
        await getBrandAssociatedPurchasersLookupUseCase.execute(brandId);
      const options: CatalogPurchaserSelectOption[] = items
        .filter(
          (purchaser) =>
            purchaser.purchaserIsActive &&
            !existingPurchaserKeys.has(purchaser.purchaserKey.toUpperCase()),
        )
        .map((purchaser) => ({
          purchaserId: purchaser.purchaserId,
          purchaserKey: purchaser.purchaserKey,
          purchaserName: purchaser.purchaserName,
          configTemplate: purchaser.configTemplate ?? null,
        }));
      setCatalogOptions(options);
      for (const option of options) {
        if (
          !hasCachedPurchaserConfigTemplate(
            brandId,
            brandKey,
            option.purchaserId,
          )
        ) {
          setCachedPurchaserConfigTemplate(
            brandId,
            brandKey,
            option.purchaserId,
            option.configTemplate,
          );
        }
      }
      catalogLoadedRef.current = true;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load brand associated purchasers";
      errorToast({ content: message });
      setCatalogOptions([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [
    brandId,
    brandKey,
    errorToast,
    existingPurchaserKeys,
    getBrandAssociatedPurchasersLookupUseCase,
    modalMode,
  ]);

  const getCachedConfigTemplate = useCallback(
    (purchaserId: number) =>
      getCachedPurchaserConfigTemplate(brandId, brandKey, purchaserId),
    [brandId, brandKey],
  );

  const loadConfigTemplate = useCallback(
    async (purchaserId: number) => {
      try {
        const template = await getOrFetchPurchaserConfigTemplate(
          brandId,
          brandKey,
          purchaserId,
          () => getConfigTemplateUseCase.execute(purchaserId),
        );
        setCatalogOptions((prev) =>
          prev.map((option) =>
            option.purchaserId === purchaserId
              ? { ...option, configTemplate: template }
              : option,
          ),
        );
        return template;
      } catch (err) {
        errorToast({
          content:
            err instanceof Error
              ? err.message
              : "Failed to load purchaser config template",
        });
        return null;
      }
    },
    [brandId, brandKey, errorToast, getConfigTemplateUseCase],
  );

  useEffect(() => {
    if (modalMode == null) return;
    void ensureCategoriesForModal();
    void ensureCatalogPurchasersForModal();
  }, [ensureCatalogPurchasersForModal, ensureCategoriesForModal, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const handleSubmit = useCallback(
    async (values: PurchaserAddEditFormValues) => {
      setIsSaving(true);
      try {
        const identity = resolvePurchaserIdentity(
          values,
          modalMode,
          catalogOptions,
        );
        if (!identity) {
          form.setFields([
            {
              name:
                modalMode === "create" ? "catalogPurchaserId" : "purchaserName",
              errors: ["Purchaser name is required"],
            },
          ]);
          setIsFormSubmittable(false);
          return;
        }

        const fields = parsePurchaserSaveFields(values);
        if (modalMode === "edit" && editingPurchaser) {
          await updateUseCase.execute(
            new UpdateMasterDataPurchaserDTO({
              purchaserId: editingPurchaser.purchaserId,
              purchaserName: identity.purchaserName,
              ...fields,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataPurchaserDTO({
              purchaserName: identity.purchaserName,
              purchaserKey: identity.purchaserKey,
              ...fields,
            }),
          );
        }

        successToast({
          content: "Purchaser saved successfully",
        });
        catalogLoadedRef.current = false;
        closeModal();
        invalidateMasterDataListCacheScopes(["purchasers-lookup"]);
        refetch();
      } catch (err) {
        applyPurchaserSaveError(
          err,
          form,
          modalMode,
          (content) => errorToast({ content }),
          () => setIsFormSubmittable(false),
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      catalogOptions,
      createUseCase,
      editingPurchaser,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  let subtitle = "Loading...";
  if (!loading) {
    const recordCount = purchasers.length;
    subtitle = recordCount === 1 ? "1 record" : `${recordCount} records`;
  }

  return (
    <>
      <Card
        className="master-data-list-page bg-white"
        variant="outlined"
        shadow
        padding="small"
      >
        <Flex
          align="center"
          justifyContent="space-between"
          gap={0.75}
          className="master-data-list-page__header mb-3"
        >
          <Flex direction="column" gap={0.15}>
            <Heading level={6} className="text-grey-700 font-bold m-0">
              Purchasers
            </Heading>
            <Paragraph className="text-grey-500 m-0">{subtitle}</Paragraph>
          </Flex>
          <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_ADD]}>
            <Button size="small" variant="outline" onPress={openCreateModal}>
              <Icon slot="start" name={Icons.Plus} />
              Add Purchaser
            </Button>
          </HasAnyPermission>
        </Flex>

        <Division className="master-data-list-page__body">
          {loading ? (
            <Flex align="center" justifyContent="center" className="py-6">
              <Spin tip="Loading..." spinning />
            </Flex>
          ) : (
            <MasterDataPurchasersMatrixTable
              purchasers={purchasers}
              onViewJson={(title, value) => setJsonPreview({ title, value })}
              onViewDc={setDcPreview}
              onEdit={openEditModal}
            />
          )}
        </Division>
      </Card>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Purchaser"
        editTitle="Edit Purchaser"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataPurchaserAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          catalogOptions={catalogOptions}
          catalogLoading={catalogLoading}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
          onLoadConfigTemplate={loadConfigTemplate}
          getCachedConfigTemplate={getCachedConfigTemplate}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <MasterDataJsonPreviewModal
        isOpen={jsonPreview != null}
        title={jsonPreview?.title ?? ""}
        value={jsonPreview?.value}
        onClose={() => setJsonPreview(null)}
      />

      <MasterDataPurchaserDcModal
        isOpen={dcPreview != null}
        purchaserId={dcPreview?.purchaserId ?? null}
        purchaserName={dcPreview?.purchaserName ?? ""}
        onClose={() => setDcPreview(null)}
      />
    </>
  );
};
